import React, { useState, forwardRef } from "react";
import ReactSelect from "react-select";
import AsyncReactSelect from "react-select/async";
import CreatableReactSelect from "react-select/creatable";
import AsyncCreatableReactSelect from "react-select/async-creatable";
import NoSsr from "@mui/material/NoSsr";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material";
import useTranslation from "next-translate/useTranslation";
import Control from "./Control";
import Option from "./Option";
import MultiValue from "./MultiValue";
import Placeholder from "./Placeholder";
import SingleValue from "./SingleValue";
import NoOptionsMessage from "./NoOptionsMessage";
import DropdownIndicator from "./DropdownIndicator";
import IndicatorSeparator from "./IndicatorSeparator";
import ClearIndicator from "./ClearIndicator";
import ValueContainer from "./ValueContainer";
import Menu from "./Menu";
import { Text } from "@utils/helper";
import { buildClass } from "@utils/tag";

const Select = forwardRef(
  (
    {
      id,
      label,
      placeholder,
      margin,
      errorText,
      error,
      isAsync,
      isCreatable,
      components,
      optional = false,
      inputProps,
      customLabel,
      classNameLabel,
      onChange,
      changeCallback,
      bgc,
      shadow,
      optionColor,
      ...otherProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);

    const focusHandler = () => {
      setIsFocused(true);
      if (otherProps?.onFocus) otherProps.onFocus();
    };

    const blurHandler = () => {
      setIsFocused(false);
      if (otherProps?.onBlur) otherProps.onBlur();
    };

    const selectStyles = {
      singleValue: (base) => ({ ...base, marginRight: 0, marginLeft: 0 }),
      input: (base) => ({
        ...base,
        margin: 0,
        color: theme.palette.text.primary,
        "& input": { font: "inherit" },
      }),
      menu: (base) => ({ ...base, bgc: "#F8FBFF", zIndex: 5000 }),
      menuPortal: (base) => ({ ...base, zIndex: 5000 }),
      option: (base) => ({
        ...base,
        backgroundColor: "#0366FF !important",
        color: "#fff !important",
      }),
    };

    const SelectControl = isAsync
      ? isCreatable
        ? AsyncCreatableReactSelect
        : AsyncReactSelect
      : isCreatable
      ? CreatableReactSelect
      : ReactSelect;

    const labelWithOptional = optional ? `${label} (${t("optional")})` : label;

    const noOptionsMessage = () => {
      return otherProps.noOpts ? (
        <>{otherProps.noOpts}</>
      ) : (
        <Text size="14px" className="px-3" color="#8F8F8F">
          {t("noOptionsMessage")}
        </Text>
      );
    };

    const componentsRender = {
      Control,
      MultiValue,
      NoOptionsMessage,
      Option,
      Placeholder,
      SingleValue,
      ValueContainer,
      DropdownIndicator,
      ClearIndicator,
      IndicatorSeparator,
      Menu,
      ...components,
    };

    return (
      <NoSsr
        fallback={
          <TextField
            id={id}
            label={labelWithOptional}
            variant="outlined"
            margin={margin}
            fullWidth
            value="..."
            error={error}
            helperText={errorText}
            disabled
          />
        }
      >
        <div className="flex flex-col gap-3 relative w-full">
          {customLabel && (
            <Text
              fontType="Expo_sim"
              className={buildClass(
                classNameLabel,
                "!text-[13px] sm:!text-[16px]"
              )}
            >
              {customLabel}
            </Text>
          )}
          <div>
            <SelectControl
              ref={ref}
              styles={selectStyles}
              inputId={id}
              TextFieldProps={{
                label: labelWithOptional,
                fullWidth: true,
                variant: "outlined",
                onBlur: blurHandler,
                margin,
                error,
                bgc,
                shadow,
                placeholder,
                InputLabelProps: {
                  htmlFor: id,
                  shrink: otherProps?.value?.length > 0 || isFocused,
                },
                disabled: otherProps.disabled,
                ...inputProps,
              }}
              optionProps={{
                color: optionColor,
              }}
              placeholder={placeholder}
              onFocus={focusHandler}
              noOptionsMessage={noOptionsMessage}
              components={componentsRender}
              onChange={(e) => {
                changeCallback && changeCallback(e?.value);
                onChange(e);
              }}
              menuPlacement="auto"
              {...otherProps}
            />
            {errorText && (
              <span className="mx-[14px] mt-3 text-[12px] text-[#d32f2f] text-left">
                {errorText}
              </span>
            )}
          </div>
        </div>
      </NoSsr>
    );
  }
);

Select.displayName = "Select";

export default Select;
